// Match store — snapshot vindo do WS via `lastMessage` -> `lobby.ts`
// dispatcha. Mesa 1v1 (Fase 3) e 2v2 (Fase 5) subscrevem $matchSnapshot
// e renderizam.
//
// Tipos espelham view.go no backend. Mantemos campos opcionais pq snaps
// 1v1 nao tem team_a/team_b e vice-versa.

import { writable, derived } from 'svelte/store';
import { userId } from './auth';

export type Card = {
  value?: string;
  suit?: string;
  played?: boolean;
  flipped?: boolean;
};

export type PlayerSnapshot = {
  id: string;
  name: string;
  game_score?: number;
  ranking_score?: number;
  avatar?: string;
  cards?: Card[];
  first_play?: Card;
  second_play?: Card;
  third_play?: Card;
};

export type MatchSnapshot = {
  id: string;
  modality: string;
  format: '1v1' | '2v2';
  status: 'running' | 'finished';
  next_to_play_id: string;
  p1: PlayerSnapshot;
  p2: PlayerSnapshot;
  team_a?: PlayerSnapshot[];
  team_b?: PlayerSnapshot[];
  team_a_score?: number;
  team_b_score?: number;
  manilha?: Card | null;
  round_points: number;
  first_round_winner?: string;
  truco_challenger_id?: string;
  truco_challenge_running: boolean;
  last_escalator_id?: string;
  truco_locked?: boolean;
  mano_especial_active?: boolean;
  mano_especial_player_id?: string;
  mano_especial_pending?: boolean;
  // mano_especial_last_decider: pid de quem aceitou/fugiu na ultima mao
  // de 11 (bot OU humano). Front mostra bubble nele.
  mano_especial_last_decider?: string;
  // "aceitar" ou "fugir"
  mano_especial_last_decision?: string;
  // truco_last_refuser: pid de quem correu do truco mais recente. Bubble.
  truco_last_refuser?: string;
  truco_last_acceptor?: string;
  round_winner?: string;
  round_value?: number;
  round_dead?: boolean;
  // round_number: 1-based index da mao atual. Backend incrementa em
  // dealNextRound. Frontend usa pra dedup de toasts (ex: "MAO MORTA"
  // dispara so uma vez por mao, mesmo com varios snaps com round_dead=true).
  round_number?: number;
  // tricks_closed: quantas vazas ja fecharam na mao atual (0, 1 ou 2 —
  // a 3a fecha a mao). Backend envia desde 2026-05-18 pra que o front
  // detecte "1o trick" sem depender de contar first/second/third_play,
  // que com snapshot delta (B1) so popula a vaza atual.
  tricks_closed?: number;
  // trick_winners: vencedor de cada vaza fechada da mao atual (em ordem):
  // "A", "B" ou "tie". Permite o Scoreboard manter a cor das rodadas
  // depois que o trick fecha (snapshot delta limpa first/second/third_play).
  trick_winners?: string[];
  winner?: string;
  deadline?: string; // ISO timestamp
  turn_duration_sec?: number;
};

export const matchSnapshot = writable<MatchSnapshot | null>(null);

// `inMatch` indica que ha uma partida em curso (qualquer status).
// App.svelte usa pra rotear pra view de game.
export const inMatch = derived(matchSnapshot, ($s) => $s !== null);

// Helper: encontrar o player viewer no snap. Em 1v1 olha p1/p2;
// em 2v2 olha team_a/team_b. Retorna null se nao encontrar (espectador
// ou snap stale).
export const myPlayer = derived(
  [matchSnapshot, userId],
  ([$snap, $uid]): PlayerSnapshot | null => {
    if (!$snap || !$uid) return null;
    if ($snap.format === '2v2') {
      const a = ($snap.team_a || []).find((p) => p.id === $uid);
      if (a) return a;
      const b = ($snap.team_b || []).find((p) => p.id === $uid);
      if (b) return b;
      return null;
    }
    if ($snap.p1?.id === $uid) return $snap.p1;
    if ($snap.p2?.id === $uid) return $snap.p2;
    return null;
  },
);
